import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { PersistPreferredLocale } from '@platform-frontend/transloco';
import { SetIsLoading } from '@platform-frontend/ui-tools';
import { CloseWindow, PlannedError, ShowErrorNotification, StorageService, handleErrors, throwAndHandleError } from '@platform-frontend/util';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ACCOUNT_ERROR_HANDLERS } from '../account-error-handlers';
import { AuthenticationServiceToken, IAuthenticationService } from '../account-services';
import { SetAuthFormEmail } from '../auth-form';
import { LinkAccountState } from '../link-account';
import { LINK_ACCOUNT_ERROR_HANDLERS } from '../link-account/link-account-error-handlers';
import {
  MSG_SIGN_IN_DEFAULT,
  MSG_SIGN_IN_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS,
  MSG_SIGN_IN_INVALID_PASSWORD,
  MSG_SIGN_IN_USER_NOT_KNOWN
} from '../notification-messages';
import { RequestPasswordUpdate } from '../password-update/password-update.actions';
import { AccountLinking } from './model/account-linking';
import { Session } from './model/session';
import { SessionStateModel } from './model/session-state-model';
import { SignOutFollowUp } from './model/sign-out-follow-up';
import {
  CompareSessionWithLinkedAccount,
  GotoSignIn,
  SessionSet,
  SetSession,
  SignIn,
  SignOut,
  SignOutOnError,
  UpdateSessionFromBrowser
} from './session.actions';

@State<SessionStateModel>({
  name: 'session',
  defaults: {
    session: null
  }
})
@Injectable()
export class SessionState {
  constructor(
    @Inject(AuthenticationServiceToken) @Optional() private authenticationService: IAuthenticationService | null,
    private http: HttpClient,
    private storageService: StorageService,
    private store: Store
  ) {}

  linkedAccountId$: Observable<string | null> = this.store.select(LinkAccountState.accountId);
  linkedAccountEmail$: Observable<string | null> = this.store.select(LinkAccountState.email);

  @Selector()
  static session(state: SessionStateModel): Session | null {
    return state.session;
  }

  @Selector()
  static isSignedIn(state: SessionStateModel): boolean {
    return state.session !== null;
  }

  @Selector()
  static token(state: SessionStateModel): string | null {
    return state.session?.token ?? null;
  }

  @Selector()
  static email(state: SessionStateModel): string | null {
    return state.session?.email ?? null;
  }

  @Selector()
  static isDisabled(state: SessionStateModel): boolean | null {
    return state.session?.disabled ?? null;
  }

  @Selector()
  static accessToVendor(state: SessionStateModel): boolean | null {
    return state.session?.accessToVendor ?? null;
  }

  @Selector()
  static accountLinking(state: SessionStateModel): AccountLinking | null {
    return state.session?.accountLinking ?? null;
  }

  @Selector([SessionState.accountLinking])
  static accountLinked(state: SessionStateModel, accountLinking: AccountLinking | null): boolean {
    return (accountLinking?.accountId ?? null) !== null;
  }

  @Action(SignIn)
  signIn(ctx: StateContext<SessionStateModel>, { credentials }: SignIn): Observable<unknown> {
    return this.linkedAccountId$.pipe(
      take(1),
      switchMap(linkedAccountId => this.http.post<Session>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/login', { ...credentials, linkedAccountId })),
      switchMap(session => ctx.dispatch(new SetSession(session))),
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        DEFAULT: new ShowErrorNotification(MSG_SIGN_IN_DEFAULT),
        DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS: new ShowErrorNotification(MSG_SIGN_IN_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS(credentials.email)),
        INSECURE_PASSWORD: new RequestPasswordUpdate(credentials.email, true),
        INVALID_PASSWORD: new ShowErrorNotification(MSG_SIGN_IN_INVALID_PASSWORD),
        USER_NOT_KNOWN: new ShowErrorNotification(MSG_SIGN_IN_USER_NOT_KNOWN),
        ...LINK_ACCOUNT_ERROR_HANDLERS(this.store)
      })
    );
  }

  @Action(UpdateSessionFromBrowser)
  updateSessionFromBrowser(ctx: StateContext<SessionStateModel>): Observable<unknown> {
    return ctx.dispatch(new SetSession(this.storageService.getFromLocalStorage<Session>('session.session') ?? null));
  }

  @Action(SetSession)
  setSession(ctx: StateContext<SessionStateModel>, { session }: SetSession): Observable<unknown> {
    ctx.patchState({ session });

    return this.compareSessionWithLinkedAccount(ctx).pipe(
      switchMap(() => (session !== null ? ctx.dispatch(new SetAuthFormEmail(session.email)) : of(null))),
      switchMap(() => {
        return ctx.dispatch(new SessionSet(session !== null, session?.disabled ?? null));
      })
    );
  }

  @Action(PersistPreferredLocale)
  persistPreferredLocale(ctx: StateContext<SessionStateModel>, { locale }: PersistPreferredLocale): Observable<unknown> {
    return ctx.getState().session
      ? this.http.put<void>(PLATFORM_ENVIRONMENT.apiUrl + '/master-data/preferred-locale', { preferredLocale: locale }).pipe(catchError(() => of(null)))
      : of(null);
  }

  @Action(CompareSessionWithLinkedAccount)
  compareSessionWithLinkedAccount(ctx: StateContext<SessionStateModel>): Observable<unknown> {
    const session = ctx.getState().session;
    if (session) {
      return this.throwErrorForAccountConflict(session.accountLinking?.accountId ?? null);
    } else {
      return this.resetToLinkedAccountEmail(ctx);
    }
  }

  private throwErrorForAccountConflict(linkedAccountIdFromSession: string | null): Observable<unknown> {
    return this.linkedAccountId$.pipe(
      take(1),
      map(linkedAccountId => linkedAccountIdFromSession !== null && linkedAccountId !== null && linkedAccountIdFromSession !== linkedAccountId),
      switchMap(accountConflict =>
        accountConflict
          ? throwAndHandleError(LINK_ACCOUNT_ERROR_HANDLERS(this.store).LINK_ACCOUNT_MISMATCH, new PlannedError('LINK_ACCOUNT_MISMATCH'))
          : of(null)
      )
    );
  }

  @Action(SignOut)
  signOut(ctx: StateContext<SessionStateModel>, { followUp }: SignOut): Observable<unknown> {
    return this.logout(ctx).pipe(switchMap(() => this.handleSignOutFollowUp(ctx, followUp)));
  }

  private logout(ctx: StateContext<SessionStateModel>): Observable<unknown> {
    const session = ctx.getState().session;

    if (session === null) {
      return of(null);
    }

    return ctx.dispatch(new SetSession(null)).pipe(
      switchMap(() => ctx.dispatch(new SetAuthFormEmail(session.email))),
      switchMap(() => this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/logout', { token: session.token })),
      catchError(() => of(null))
    );
  }

  private handleSignOutFollowUp(ctx: StateContext<SessionStateModel>, followUp: SignOutFollowUp): Observable<unknown> {
    switch (followUp) {
      case 'authenticate':
        return ctx.dispatch(new GotoSignIn());
      case 'closeOrAuthenticate':
        return ctx.dispatch(this.authenticationService?.closeApplicationOnSignOut() ?? true ? new CloseWindow() : new GotoSignIn());
      case 'none':
      default:
        return EMPTY;
    }
  }

  @Action(SignOutOnError)
  signOutOnError(ctx: StateContext<SessionStateModel>, { notificationAction, resetToLinkedAccount }: SignOutOnError): Observable<unknown> {
    return ctx.dispatch(new SetIsLoading(false)).pipe(
      switchMap(() => ctx.dispatch(new SignOut('none'))),
      switchMap(() => (notificationAction ? ctx.dispatch(notificationAction) : of(null))),
      switchMap(() => (resetToLinkedAccount ? this.resetToLinkedAccountEmail(ctx) : of(null))),
      switchMap(() => ctx.dispatch(new GotoSignIn({ clearNotifications: false, signInMode: 'ON_ERROR' })))
    );
  }

  private resetToLinkedAccountEmail(ctx: StateContext<SessionStateModel>): Observable<unknown> {
    return this.linkedAccountEmail$.pipe(
      take(1),
      switchMap(email => ctx.dispatch(new SetAuthFormEmail(email)))
    );
  }
}
